import { useTranslation } from "react-i18next";
import { FaMapMarkerAlt, FaWhatsapp } from "react-icons/fa";

function TutorCenterList({ centers, onSelectCenter, addresses }) {
  const { t } = useTranslation();
  // Debug: log center count to help diagnose empty list issue
  try {
    // eslint-disable-next-line no-console
    console.log(
      `TutorCenterList: centers count = ${centers ? centers.length : 0}`
    );
  } catch (e) {
    // ignore
  }
  const getAddress = (center) => {
    // Show raw address field when available (consistent with detail view)
    if (center && center.address) return center.address;
    return "Address not available";
  };

  const normalizeWhatsAppNumber = (num) => {
    if (!num) return "";
    const cleaned = num.replace(/\D/g, "");
    if (!cleaned) return "";
    // If number looks like a local HK number (8 digits), prepend country code 852
    if (cleaned.length === 8) return `852${cleaned}`;
    // If it starts with 0 and then 8 digits, strip leading zeros and prepend 852
    const withoutLeadingZeros = cleaned.replace(/^0+/, "");
    if (withoutLeadingZeros.length === 8) return `852${withoutLeadingZeros}`;
    // Otherwise return cleaned (assume it includes country code)
    return cleaned;
  };

  const isMobileDevice = () => {
    try {
      return /Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(
        navigator.userAgent
      );
    } catch (e) {
      return false;
    }
  };

  // Resolve various image reference formats:
  // - full http(s) or data URLs are returned as-is
  // - absolute paths starting with '/' are returned as-is
  // - bare filenames (e.g. "img.jpg") will be attempted to be required from src/image_center
  // - fallback to PUBLIC_URL + '/image_center/<file>' if require fails (useful when images are placed in public/)
  const resolveImageSrc = (img) => {
    if (!img) return "logo.png";
    const asString = String(img);
    // keep data and remote URLs
    if (/^data:|^https?:\/\//i.test(asString)) return asString;
    // absolute path from site root
    if (/^\//.test(asString)) return asString;

    // attempt to require from src/image_center (relative to this file)
    try {
      // require returns a module object in some setups; prefer default if present
      // eslint-disable-next-line global-require, import/no-dynamic-require
      const mod = require(`../../image_center/${asString}`);
      return mod && mod.default ? mod.default : mod;
    } catch (e) {
      // fallthrough to PUBLIC_URL fallback
    }

    // fallback: assume image is in public/image_center
    try {
      const pub = (process.env.PUBLIC_URL || "") + `/image_center/${asString}`;
      return pub;
    } catch (e) {
      return asString;
    }
  };

  // In the list view we only show the first image to keep the grid compact.
  // resolveImageSrc is used below to support local filenames or public folder images.

  const openWhatsApp = (num, message = "") => {
    const n = normalizeWhatsAppNumber(num);
    if (!n) return;
    const phoneNumber = n;
    const encodedMessage = message ? encodeURIComponent(message) : "";
    let whatsappUrl2 = "";
    if (isMobileDevice()) {
      console.log("Mobile device detected");
      whatsappUrl2 = `https://api.whatsapp.com/send?phone=${phoneNumber}${
        encodedMessage ? `&text=${encodedMessage}` : ""
      }`;
    } else {
      console.log("Desktop device detected");
      whatsappUrl2 = `https://web.whatsapp.com/send?phone=${phoneNumber}${
        encodedMessage ? `&text=${encodedMessage}` : ""
      }`;
    }
    try {
      window.open(whatsappUrl2, "_whatsapp");
    } catch (e) {
      window.location.href = whatsappUrl2;
    }
  };

  const extractPrice = (contentText) => {
    const priceMatch = contentText?.match(/HK\$\s*\d+/);
    return priceMatch ? priceMatch[0] : null;
  };

  if (centers.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">
          {t("noResults") ||
            "No tutor centers found. Try a different search term."}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {centers.map((center, index) => (
        <div
          key={index}
          className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden cursor-pointer"
          onClick={() => {
            try {
              if (typeof onSelectCenter === "function") onSelectCenter(center);
            } catch (e) {}
            // save current vertical scroll so Back returns user to the same place
            try {
              const pos = window.scrollY || window.pageYOffset || 0;
              sessionStorage.setItem("tutorCenters_scroll", String(pos));
            } catch (e) {}
            // navigate to independent detail page including slug for SEO
            try {
              const slug =
                center && center.title
                  ? center.title
                      .toString()
                      .toLowerCase()
                      .normalize("NFKD")
                      // remove combining diacritical marks (safe replacement for \p{Diacritic})
                      .replace(/[\u0300-\u036f]/g, "")
                      // keep latin letters, numbers and common CJK ranges so Chinese in titles is preserved
                      .replace(/[^a-z0-9\u4e00-\u9fff]+/g, "-")
                      .replace(/-+/g, "-")
                      .replace(/^-|-$/g, "")
                  : (center && center.id) || "";
              if (center && center.id)
                window.open(`/tutorCenters/${center.id}/${slug}`, "_blank");
            } catch (e) {
              if (center && center.id)
                window.open(`/tutorCenters/${center.id}`, "_blank");
            }
          }}
        >
          {/* Image: show only the first image in the list for compactness */}
          {center.images && center.images.length > 0 && center.images[0] && (
            <div className="h-48 overflow-hidden bg-gray-200">
              <img
                src={resolveImageSrc(center.images[0])}
                alt={center.title || "Tutor Center"}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          )}
          {!center.images || center.images.length === 0 ? (
            <div className="h-48 overflow-hidden bg-gray-200 flex items-center justify-center">
              <img src="logo.png" alt={center.title || "Tutor Center"} />
            </div>
          ) : null}

          {/* Content */}
          <div className="p-4">
            {/* Title */}
            <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2 min-h-[3.5rem]">
              {center.title || "Unnamed Center"}
            </h3>

            {/* Price */}
            {extractPrice(center.content_text) && (
              <div className="text-orange-600 font-semibold mb-2">
                From {extractPrice(center.content_text)}
              </div>
            )}

            {/* Address */}
            <div className="flex items-start text-gray-600 text-sm mb-2">
              <FaMapMarkerAlt className="mt-1 mr-2 flex-shrink-0 text-orange-500" />
              <span className="line-clamp-2">{getAddress(center)}</span>
            </div>

            {/* WhatsApp */}
            {center.whatsapp_number && (
              <div className="flex items-center text-gray-600 text-sm mb-3">
                <FaWhatsapp className="mr-2 text-green-500" />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openWhatsApp(center.whatsapp_number);
                  }}
                  className="text-left text-blue-600 hover:underline"
                >
                  {center.whatsapp_number}
                </button>
              </div>
            )}

            {/* Tags */}
            {center.product_tags && center.product_tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {center.product_tags.slice(0, 3).map((tag, tagIndex) => (
                  <span
                    key={tagIndex}
                    className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
                {center.product_tags.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                    +{center.product_tags.length - 3} more
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default TutorCenterList;
